import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/shared/utils/core";

function onlyDigits(s: string) {
  return s.replace(/\D/g, "").slice(0, 9);
}
function formatSSNFromRaw(raw: string) {
  const r = onlyDigits(raw);
  if (r.length <= 3) return r;
  if (r.length <= 5) return `${r.slice(0, 3)}-${r.slice(3)}`;
  return `${r.slice(0, 3)}-${r.slice(3, 5)}-${r.slice(5)}`;
}
function maskSSNFromRaw(raw: string) {
  // Keep your original behavior: mask everything typed with Xs
  const len = Math.min(onlyDigits(raw).length, 9);
  if (len === 0) return "";
  if (len <= 3) return "X".repeat(len);
  if (len <= 5) return `XXX-${"X".repeat(len - 3)}`;
  return `XXX-XX-${"X".repeat(len - 5)}`;
}
function digitToVisualIndex(digitIndex: number) {
  // Map the Nth digit's caret position into the formatted string
  // Positions: 0..2 digits, dash, 3..4 digits, dash, 5..8 digits
  if (digitIndex <= 3) return digitIndex; // before first dash
  if (digitIndex <= 5) return digitIndex + 1; // skip 1 dash after 3rd digit
  return digitIndex + 2; // skip 2 dashes after 5th digit
}
function visualToDigitIndex(formattedOrMasked: string, visualIndex: number) {
  // Count how many digits appear before the visualIndex
  let count = 0;
  for (let i = 0; i < Math.min(visualIndex, formattedOrMasked.length); i++) {
    const ch = formattedOrMasked[i];
    if (/\d/.test(ch)) count++; // visible mode
    if (ch === "X") count++; // masked mode (treat X as a digit placeholder)
  }
  return Math.min(count, 9);
}

export default function SSNInput({
  valueFormatted,
  onChangeFormatted,
  ssnVisible,
  toggleVisible,
  className,
  isDetailMissing,
}: {
  valueFormatted: string;
  onChangeFormatted: (formatted: string) => void;
  ssnVisible: boolean;
  toggleVisible: () => void;
  className?: string;
  isDetailMissing: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [raw, setRaw] = useState<string>(() => onlyDigits(valueFormatted));
  const nextDigitCaretRef = useRef<number | null>(null);
  const hasSyncedRef = useRef<boolean>(false);

  const noop = useCallback(() => {}, []);

  // Sync raw state with external valueFormatted (only once on initial mount)
  useEffect(() => {
    if (hasSyncedRef.current) return;
    const extRaw = onlyDigits(valueFormatted);
    if (extRaw && extRaw !== raw) {
      setRaw(extRaw);
      hasSyncedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueFormatted]);

  const display = useMemo(
    () => (ssnVisible ? formatSSNFromRaw(raw) : maskSSNFromRaw(raw)),
    [raw, ssnVisible],
  );

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (nextDigitCaretRef.current == null) return;

    const digitCaret = Math.max(0, Math.min(nextDigitCaretRef.current, raw.length));
    const visualCaret = digitToVisualIndex(digitCaret);
    // clamp to display length
    const pos = Math.min(visualCaret, display.length);
    el.setSelectionRange(pos, pos);
    nextDigitCaretRef.current = null;
  }, [display, raw]);

  // Visible mode: we can just read digits from typed value and compute caret from selectionStart
  function handleVisibleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const el = e.target;
    const newDisplay = el.value;
    const newDigits = onlyDigits(newDisplay);

    // Caret as "digit count before caret"
    const digitCaret = visualToDigitIndex(newDisplay, el.selectionStart ?? newDisplay.length);

    setRaw(newDigits);
    onChangeFormatted(formatSSNFromRaw(newDigits));
    // We try to keep the same digit-caret if possible
    nextDigitCaretRef.current = digitCaret;
  }

  function insertAt(rawStr: string, idx: number, ch: string) {
    if (!/\d/.test(ch) || rawStr.length >= 9) return rawStr;
    idx = Math.max(0, Math.min(idx, rawStr.length));
    return rawStr.slice(0, idx) + ch + rawStr.slice(idx);
  }
  function deleteAt(rawStr: string, idx: number) {
    // delete digit *before* idx (i.e., Backspace semantics)
    if (idx <= 0) return rawStr;
    return rawStr.slice(0, idx - 1) + rawStr.slice(idx);
  }
  function deleteForwardAt(rawStr: string, idx: number) {
    // Delete semantics
    if (idx >= rawStr.length) return rawStr;
    return rawStr.slice(0, idx) + rawStr.slice(idx + 1);
  }
  function applyInsert(rawStr: string, startDigit: number, endDigit: number, text: string) {
    const digits = text.replace(/\D/g, "");
    if (!digits) return { next: rawStr, caret: startDigit };
    // remove selection
    const base = rawStr.slice(0, startDigit) + rawStr.slice(endDigit);
    // clamp remaining capacity
    const room = 9 - base.length;
    const toAdd = digits.slice(0, Math.max(0, room));
    const next = base.slice(0, startDigit) + toAdd + base.slice(startDigit);
    return { next, caret: Math.min(startDigit + toAdd.length, 9) };
  }

  function handleMaskedBeforeInput(
    e: React.FormEvent<HTMLInputElement> & { nativeEvent: InputEvent },
  ) {
    // Intercept typed characters and insert into raw at digit caret
    const el = e.currentTarget as HTMLInputElement;
    const inputEvent = e.nativeEvent;
    const data = inputEvent.data ?? "";

    // Let browsers handle non-insert inputTypes via keydown
    if (inputEvent.inputType !== "insertText" && inputEvent.inputType !== "insertCompositionText") {
      return;
    }

    const caretDigit = visualToDigitIndex(display, el.selectionStart ?? display.length);
    if (!/^\d$/.test(data)) {
      // Only accept digits in masked mode
      e.preventDefault();
      nextDigitCaretRef.current = caretDigit;
      return;
    }

    e.preventDefault();
    const newRaw = insertAt(raw, caretDigit, data);
    setRaw(newRaw);
    onChangeFormatted(formatSSNFromRaw(newRaw));
    nextDigitCaretRef.current = Math.min(caretDigit + 1, newRaw.length);
  }

  function handleMaskedKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const el = e.currentTarget as HTMLInputElement;
    const start = el.selectionStart ?? display.length;
    const end = el.selectionEnd ?? start;
    const startDigit = visualToDigitIndex(display, start);
    const endDigit = visualToDigitIndex(display, end);
    const hasSelection = start !== end;

    // Allow navigation/editing keys
    const nav = new Set([
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Tab",
      "Meta",
      "Alt",
      "Control",
      "Shift",
    ]);
    if (nav.has(e.key)) return;

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const { next, caret } = applyInsert(
        raw,
        startDigit,
        hasSelection ? endDigit : startDigit,
        e.key,
      );
      setRaw(next);
      onChangeFormatted(formatSSNFromRaw(next));
      nextDigitCaretRef.current = caret;
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      let next = raw;
      let caret = startDigit;
      if (hasSelection) {
        next = raw.slice(0, startDigit) + raw.slice(endDigit);
        caret = startDigit;
      } else {
        next = deleteAt(raw, startDigit);
        caret = Math.max(0, startDigit - 1);
      }
      setRaw(next);
      onChangeFormatted(formatSSNFromRaw(next));
      nextDigitCaretRef.current = caret;
      return;
    }

    if (e.key === "Delete") {
      e.preventDefault();
      let next = raw;
      let caret = startDigit;
      if (hasSelection) {
        next = raw.slice(0, startDigit) + raw.slice(endDigit);
        caret = startDigit;
      } else {
        next = deleteForwardAt(raw, startDigit);
        caret = startDigit;
      }
      setRaw(next);
      onChangeFormatted(formatSSNFromRaw(next));
      nextDigitCaretRef.current = caret;
      return;
    }

    // Block other printable characters
    if (e.key.length === 1) e.preventDefault();
  }

  function handleMaskedPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const el = e.currentTarget as HTMLInputElement;
    const start = el.selectionStart ?? display.length;
    const end = el.selectionEnd ?? start;
    const startDigit = visualToDigitIndex(display, start);
    const endDigit = visualToDigitIndex(display, end);
    const { next, caret } = applyInsert(
      raw,
      startDigit,
      endDigit,
      e.clipboardData.getData("text") || "",
    );
    setRaw(next);
    onChangeFormatted(formatSSNFromRaw(next));
    nextDigitCaretRef.current = caret;
  }

  return (
    <div>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder="XXX-XX-XXXX"
          value={display}
          onChange={ssnVisible ? handleVisibleChange : noop}
          onBeforeInput={!ssnVisible ? handleMaskedBeforeInput : undefined}
          onKeyDown={!ssnVisible ? handleMaskedKeyDown : undefined}
          onPaste={!ssnVisible ? handleMaskedPaste : undefined}
          maxLength={11}
          autoComplete="off"
          inputMode="numeric"
          className={cn(className, isDetailMissing && "border-[2px] border-destructive")}
        />
        <IconButton
          onClick={toggleVisible}
          className="absolute right-1 top-1/2 -translate-y-1/2"
          tabIndex={-1}
          label={ssnVisible ? "Hide SSN" : "Show SSN"}
        >
          {ssnVisible ? <EyeOff /> : <Eye />}
        </IconButton>
      </div>
      <FieldError
        message={isDetailMissing ? "Social Security Number is required" : null}
        className="mt-1"
      />
    </div>
  );
}
